import { scrollReset, viewportResize } from 'viewport-lock';
import { IOS_TAP_SLOP_PX } from '../../../../lib/iosTapSlop';
import { canUseViewportLockLib } from './keyboardCapability';

/**
 * 开户流程 viewport-lock。
 * 仅 mode === 'viewport-lock' 时同步壳高度并复位外层滚动：iOS 15.5+，或 Android >= 139。
 * Android < 139 走 js-measure，不挂这两项。
 *
 * 不使用库的 createViewportLock / focusIntercept：它对 input 的 touchend preventDefault
 * 会取消系统聚焦，键盘和随后的 focusin 滚动都不会发生。
 * 键盘弹起后把输入框滚到键盘上方仍由 keyboardAvoid 负责，这里不改那套计算。
 */
export const OPEN_PROGRESS_VIEWPORT_LOCK = true;

const TEXT_INPUT_TYPES = new Set([
  '',
  'text',
  'search',
  'url',
  'tel',
  'email',
  'password',
  'number',
]);

let unlock: (() => void) | null = null;
let lockedEl: HTMLElement | null = null;

export function canUseViewportLock(): boolean {
  if (!OPEN_PROGRESS_VIEWPORT_LOCK) return false;
  return canUseViewportLockLib();
}

/** 是否正在使用 viewport-lock（供 keyboardAvoid 旁路 padding） */
export const isOpenProgressViewportLockActive = () => canUseViewportLock() && !!unlock && !!lockedEl;

function isKeyboardField(el: Element): boolean {
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  const type = ((el as HTMLInputElement).type || 'text').toLowerCase();
  return TEXT_INPUT_TYPES.has(type);
}

/** 点在输入壳或文本框上才护住这次轻点，避免滚动容器把抖动判成滑动。 */
function shouldGuardTap(target: EventTarget | null, root: HTMLElement): boolean {
  if (!(target instanceof Element) || !root.contains(target)) return false;
  if (target.closest('.difi-input__shell')) return true;
  const field = target.closest('input, textarea');
  return !!field && root.contains(field) && isKeyboardField(field);
}

/**
 * 容差内的 touchmove 才 preventDefault，让随后的 touchend / click 仍能聚焦。
 * 不拦截 touchend，避免再次吃掉键盘。超出容差视为滚动，交给原来的滚动容器。
 */
function attachInputTapGuard(root: HTMLElement): () => void {
  let tracking: { x: number; y: number; moved: boolean } | null = null;

  const onStart = (e: TouchEvent) => {
    const touch = e.changedTouches?.[0] ?? e.touches?.[0];
    if (!touch || !shouldGuardTap(e.target, root)) {
      tracking = null;
      return;
    }
    tracking = { x: touch.clientX, y: touch.clientY, moved: false };
  };

  const onMove = (e: TouchEvent) => {
    if (!tracking || tracking.moved) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    const dx = Math.abs(touch.clientX - tracking.x);
    const dy = Math.abs(touch.clientY - tracking.y);
    if (dx <= IOS_TAP_SLOP_PX && dy <= IOS_TAP_SLOP_PX) {
      if (e.cancelable) e.preventDefault();
      return;
    }
    tracking.moved = true;
  };

  const clear = () => {
    tracking = null;
  };

  root.addEventListener('touchstart', onStart, { capture: true, passive: true });
  root.addEventListener('touchmove', onMove, { capture: true, passive: false });
  root.addEventListener('touchend', clear, { capture: true, passive: true });
  root.addEventListener('touchcancel', clear, { capture: true, passive: true });

  return () => {
    root.removeEventListener('touchstart', onStart, { capture: true });
    root.removeEventListener('touchmove', onMove, { capture: true });
    root.removeEventListener('touchend', clear, { capture: true });
    root.removeEventListener('touchcancel', clear, { capture: true });
    tracking = null;
  };
}

export function attachOpenProgressViewportLock(el: HTMLElement | null): () => void {
  unlock?.();
  unlock = null;
  lockedEl = null;

  if (!el) {
    return () => {};
  }

  const detachTap = attachInputTapGuard(el);
  let detachLock: (() => void) | null = null;

  if (canUseViewportLock()) {
    lockedEl = el;
    el.classList.add('open-progress-viewport-lock');
    const detachResize = viewportResize(el);
    const detachScroll = scrollReset();
    detachLock = () => {
      detachResize();
      detachScroll();
      el.classList.remove('open-progress-viewport-lock');
    };
  }

  const cleanup = () => {
    if (unlock !== cleanup) return;
    detachTap();
    detachLock?.();
    unlock = null;
    lockedEl = null;
  };

  unlock = cleanup;
  return cleanup;
}
